import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import LeadCaptureForm from '../../components/LeadCaptureForm';
import './CityLanding.css';

const BASE_URL = 'https://www.buyselltaxi.com';
const PAGE_URL = `${BASE_URL}/sell-taxi`;

const FAQS = [
  {
    q: 'How do I sell my taxi online?',
    a: 'Register as a seller on Buy Sell Taxi, create a free listing with photos, price and vehicle details, and your taxi goes live to buyers searching specifically for commercial vehicles. Interested buyers contact you directly through the platform.',
  },
  {
    q: 'Is it free to list my taxi for sale?',
    a: 'Yes. Listing your taxi or commercial vehicle on Buy Sell Taxi is free. You only pay optional charges if you choose featured or sponsored placement for faster sales. BST Verified listings get priority visibility.',
  },
  {
    q: 'How fast can I sell my taxi on Buy Sell Taxi?',
    a: 'Most sellers start receiving buyer enquiries within hours of posting. Well-photographed, fairly priced listings of popular models like Dzire, Innova, Ertiga and Eeco typically sell within a few days.',
  },
  {
    q: 'What documents do I need to sell my commercial vehicle?',
    a: 'Keep your RC book, insurance papers, PUC certificate, commercial permit, fitness certificate and any loan/NOC documents ready. Sharing these upfront builds buyer trust and helps you close the sale faster.',
  },
  {
    q: 'How do I get the best price when I sell my used taxi?',
    a: 'Upload clear photos from multiple angles, mention service history and CNG/diesel fitment, set a competitive price based on similar listings, and respond quickly to enquiries. BST Verified status and a complete listing fetch higher offers.',
  },
  {
    q: 'Can I sell taxis, mini vans, buses and other commercial vehicles?',
    a: 'Yes. Buy Sell Taxi is a full commercial vehicle marketplace. You can sell taxis, cabs, CNG and diesel vehicles, mini vans, tempo travellers, autos and buses — to buyers and fleet operators across India.',
  },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
      { '@type': 'ListItem', position: 2, name: 'Sell Your Taxi', item: PAGE_URL },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Sell used taxi and commercial vehicle online',
    provider: { '@type': 'Organization', name: 'Buy Sell Taxi', url: BASE_URL },
    areaServed: { '@type': 'Country', name: 'India' },
    description: 'List and sell used taxis, cabs, mini vans and commercial vehicles online to verified buyers across India.',
  },
];

const SellTaxi = () => {
  useSEO({
    title: 'Sell Your Taxi Online Free in India – Get Buyers Fast',
    description:
      'Sell your taxi online for free on India\'s trusted marketplace. List your used taxi, cab or commercial vehicle and reach buyers looking for commercial vehicles. Free listing, fast sale.',
    keywords:
      'sell taxi, sell taxi online, sell my taxi, sell used taxi India, sell commercial vehicle, sell my cab, buy and sell taxi, sell taxi India, list taxi for sale, taxi for sale India, sell CNG taxi, sell second hand taxi, where to sell my taxi',
    url: PAGE_URL,
    image: `${BASE_URL}/og/landing/sell-taxi.png`,
    jsonLd,
  });

  return (
    <div className="city-landing">

      {/* Hero */}
      <section className="city-hero">
        <div className="city-hero-content">
          <h1>Sell Your Taxi Online in India</h1>
          <p className="city-hero-sub">
            List your used taxi, cab or commercial vehicle for free and reach buyers who are looking for exactly that.
          </p>
          <div className="city-hero-ctas">
            <Link to="/seller/register" className="city-btn-primary">
              List Your Taxi Free
            </Link>
            <Link to="/seller/login" className="city-btn-secondary">
              Seller Login
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="city-intro">
        <div className="city-intro-inner">
          <h2>Sell Used Taxis &amp; Commercial Vehicles Faster</h2>
          <p>
            Want to sell your taxi quickly and at the best price? Buy Sell Taxi is India's trusted platform to
            buy and sell used taxis, cabs, CNG and diesel vehicles, mini vans, tempo travellers and buses.
            Post a free listing and connect directly with genuine buyers and fleet operators — no middlemen,
            no hidden charges. Whether you're an individual driver upgrading your cab or a fleet owner clearing
            inventory, you'll find the right buyer here.
          </p>
          <p>
            Not sure what to ask? Check the going rate first with the{' '}
            <Link to="/taxi-valuation">taxi valuation calculator</Link>.
          </p>
          <Link to="/seller/register" className="city-btn-primary">
            Start Selling – It's Free
          </Link>
        </div>
      </section>

      {/* How it works */}
      <section className="city-how">
        <h2>How to Sell Your Taxi in 3 Steps</h2>
        <div className="city-steps">
          <div className="city-step">
            <span className="city-step-num">1</span>
            <h3>Create a Free Listing</h3>
            <p>Register as a seller, add clear photos, set your price and list your taxi in minutes.</p>
          </div>
          <div className="city-step">
            <span className="city-step-num">2</span>
            <h3>Get Buyer Enquiries</h3>
            <p>Verified buyers across India see your listing and contact you directly — often within hours.</p>
          </div>
          <div className="city-step">
            <span className="city-step-num">3</span>
            <h3>Close the Deal</h3>
            <p>Negotiate, complete the paperwork and hand over your vehicle with BST support throughout.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="city-faq">
        <div className="city-faq-inner">
          <h2>Frequently Asked Questions – Selling Your Taxi</h2>
          <div className="city-faq-list">
            {FAQS.map(({ q, a }, i) => (
              <details key={i} className="city-faq-item">
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="city-cta-footer">
        <h2>Ready to Sell Your Taxi?</h2>
        <p>List on India's trusted commercial vehicle marketplace. Free listing, real buyers.</p>
        <div className="city-hero-ctas">
          <Link to="/seller/register" className="city-btn-primary">
            List Your Vehicle Free
          </Link>
          <Link to="/vehicles" className="city-btn-secondary">
            Browse Listings
          </Link>
        </div>
        {/* Creating a seller account is a lot to ask of someone who just wants
            to know what their taxi is worth. Sellers are also our inventory,
            so the cheapest possible path to a conversation matters here. */}
        <div className="city-cta-leadform">
          <LeadCaptureForm
            source="sell-taxi"
            requestType="seller_lead"
            heading="Don't want to create an account?"
            subheading="Tell us about your taxi and we'll call you with a valuation and buyer interest."
            buttonText="Get a Free Valuation Call"
            messagePlaceholder="e.g. 2019 Swift Dzire Tour, CNG, 80,000 km, Delhi T-permit (optional)"
          />
        </div>
      </section>

    </div>
  );
};

export default SellTaxi;
